/**
 * Saved roadmaps.
 *
 * Kept on the device deliberately: a roadmap is composed from two canonical
 * facts (archetype + band) plus hand-written copy, so nothing here needs to
 * live in the database to be reproducible. Saving records the learner's
 * choice and the moment they made it.
 */
import type { RoadmapKind } from "@/features/roadmap/roadmaps";
import type { Archetype } from "@/features/assessment-v2/archetypes";
import type { Band } from "@/features/assessment-v2/band";
import { cloudStorage } from "@/lib/cloudStore";

const KEY = "wcc360.saved_roadmaps";

export interface SavedRoadmap {
  id: string;
  kind: RoadmapKind;
  archetype: Archetype;
  band: Band;
  savedAt: string;
}

export function roadmapId(kind: RoadmapKind, archetype: Archetype, band: Band): string {
  return `${kind}:${archetype}:${band}`;
}

export function readSavedRoadmaps(): SavedRoadmap[] {
  try {
    const raw = cloudStorage.getItem(KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? (arr as SavedRoadmap[]) : [];
  } catch {
    return [];
  }
}

function write(list: SavedRoadmap[]): void {
  try {
    cloudStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* storage full or unavailable — the UI stays usable */
  }
}

export function isSaved(kind: RoadmapKind, archetype: Archetype, band: Band): boolean {
  const id = roadmapId(kind, archetype, band);
  return readSavedRoadmaps().some((r) => r.id === id);
}

export function saveRoadmap(kind: RoadmapKind, archetype: Archetype, band: Band): SavedRoadmap[] {
  const id = roadmapId(kind, archetype, band);
  const list = readSavedRoadmaps().filter((r) => r.id !== id);
  const next = [{ id, kind, archetype, band, savedAt: new Date().toISOString() }, ...list];
  write(next);
  return next;
}

export function removeRoadmap(kind: RoadmapKind, archetype: Archetype, band: Band): SavedRoadmap[] {
  const id = roadmapId(kind, archetype, band);
  const next = readSavedRoadmaps().filter((r) => r.id !== id);
  write(next);
  return next;
}
